import { canWriteSchoolScopedDataCache } from './schoolDataCacheVersion';
import {
  ASSIGNMENT_COVER_IMAGE_MAX,
  DEFAULT_ASSIGNMENT_POINTS_SETTINGS,
  emptyAssignmentPointsNoteDoc,
  type AssignmentCoverImage,
  type AssignmentPointsItem,
  type AssignmentPointsNoteDoc,
  type AssignmentPointsSettings,
  type AssignmentStudentStatus,
  type AssignmentSubTask,
} from '../types/assignmentPointsNote';

function storageKey(workspaceId: string, gradeId: string): string {
  return `cp.assignmentPointsNote.v1.${workspaceId}.${gradeId}`;
}

function normalizeStatus(raw: unknown): AssignmentStudentStatus | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const studentId = typeof o.studentId === 'string' ? o.studentId : '';
  if (!studentId) return null;
  const statusRaw = String(o.status ?? 'pending');
  const status =
    statusRaw === 'confirmed' ||
    statusRaw === 'delayed' ||
    statusRaw === 'exempted' ||
    statusRaw === 'excluded' ||
    statusRaw === 'submitted'
      ? statusRaw
      : 'pending';
  const earnedItemIds = Array.isArray(o.earnedItemIds)
    ? o.earnedItemIds.filter((x): x is string => typeof x === 'string')
    : undefined;
  return {
    studentId,
    status,
    earnedPoints: Number(o.earnedPoints) || 0,
    earnedCoins: o.earnedCoins != null ? Number(o.earnedCoins) || 0 : undefined,
    earnedItemIds,
    memo: typeof o.memo === 'string' ? o.memo : undefined,
    updatedAt: typeof o.updatedAt === 'string' ? o.updatedAt : undefined,
  };
}

function normalizeSettings(raw: unknown): AssignmentPointsSettings {
  const base = {
    ...DEFAULT_ASSIGNMENT_POINTS_SETTINGS,
    categories: DEFAULT_ASSIGNMENT_POINTS_SETTINGS.categories.map((c) => ({ ...c })),
  };
  if (!raw || typeof raw !== 'object') return base;
  const o = raw as Record<string, unknown>;
  const sdp = o.statusDefaultPoints && typeof o.statusDefaultPoints === 'object'
    ? (o.statusDefaultPoints as Record<string, unknown>)
    : {};
  const cats = Array.isArray(o.categories)
    ? o.categories
        .map((c) => {
          if (!c || typeof c !== 'object') return null;
          const r = c as Record<string, unknown>;
          const id = typeof r.id === 'string' ? r.id : '';
          if (!id) return null;
          return {
            id,
            name: typeof r.name === 'string' ? r.name : '카테고리',
            color: typeof r.color === 'string' ? r.color : '#10504a',
          };
        })
        .filter((c): c is NonNullable<typeof c> => c != null)
    : base.categories;
  return {
    defaultPoints: Number(o.defaultPoints) || base.defaultPoints,
    statusDefaultPoints: {
      pending: Number(sdp.pending) || 0,
      confirmed: Number(sdp.confirmed) || base.statusDefaultPoints.confirmed,
      delayed: Number.isFinite(Number(sdp.delayed)) ? Number(sdp.delayed) : base.statusDefaultPoints.delayed,
      exempted: Number.isFinite(Number(sdp.exempted)) ? Number(sdp.exempted) : base.statusDefaultPoints.exempted,
    },
    categories: cats.length > 0 ? cats : base.categories,
  };
}

function normalizeItem(raw: unknown): AssignmentPointsItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = typeof o.id === 'string' ? o.id : '';
  const kind = o.kind;
  if (!id) return null;
  const assignedMemberIds = Array.isArray(o.assignedMemberIds)
    ? o.assignedMemberIds.filter((x): x is string => typeof x === 'string')
    : [];
  const createdAt = typeof o.createdAt === 'string' ? o.createdAt : new Date().toISOString();
  const updatedAt = typeof o.updatedAt === 'string' ? o.updatedAt : createdAt;
  const title = typeof o.title === 'string' ? o.title : '';
  const notes = typeof o.notes === 'string' ? o.notes : undefined;
  const coverImageId = typeof o.coverImageId === 'string' && o.coverImageId.trim() ? o.coverImageId.trim() : undefined;

  if (kind === 'general') {
    const rewardItemIds = Array.isArray(o.rewardItemIds)
      ? o.rewardItemIds.filter((x): x is string => typeof x === 'string')
      : undefined;
    return {
      id,
      kind: 'general',
      title,
      dueDate: typeof o.dueDate === 'string' ? o.dueDate : '',
      basePoints: Number(o.basePoints) || 0,
      categoryId: typeof o.categoryId === 'string' ? o.categoryId : undefined,
      notes,
      coverImageId,
      rewardEnabled: o.rewardEnabled === true,
      rewardCoins: o.rewardCoins != null ? Number(o.rewardCoins) || 0 : undefined,
      rewardItemIds,
      assignedMemberIds,
      studentStatuses: Array.isArray(o.studentStatuses)
        ? o.studentStatuses.map(normalizeStatus).filter((s): s is AssignmentStudentStatus => s != null)
        : [],
      createdAt,
      updatedAt,
    };
  }

  if (kind === 'project') {
    const subTasks: AssignmentSubTask[] = Array.isArray(o.subTasks)
      ? o.subTasks
          .map((s, i) => {
            if (!s || typeof s !== 'object') return null;
            const r = s as Record<string, unknown>;
            const sid = typeof r.id === 'string' ? r.id : '';
            if (!sid) return null;
            return {
              id: sid,
              title: typeof r.title === 'string' ? r.title : '',
              points: Number(r.points) || 0,
              order: Number(r.order) || i,
            };
          })
          .filter((s): s is AssignmentSubTask => s != null)
      : [];
    const bySub: Record<string, AssignmentStudentStatus> = {};
    if (o.studentStatusesBySubTask && typeof o.studentStatusesBySubTask === 'object') {
      Object.entries(o.studentStatusesBySubTask as Record<string, unknown>).forEach(([k, v]) => {
        const st = normalizeStatus(v);
        if (st) bySub[k] = st;
      });
    }
    const projectRewardItemIds = Array.isArray(o.rewardItemIds)
      ? o.rewardItemIds.filter((x): x is string => typeof x === 'string')
      : undefined;
    const projectGrantedIds = Array.isArray(o.rewardGrantedStudentIds)
      ? o.rewardGrantedStudentIds.filter((x): x is string => typeof x === 'string')
      : undefined;
    const challengeRaw =
      o.challenge && typeof o.challenge === 'object' ? (o.challenge as Record<string, unknown>) : null;
    const challengeProjectId =
      challengeRaw && typeof challengeRaw.projectId === 'string' ? challengeRaw.projectId.trim() : '';
    return {
      id,
      kind: 'project',
      title,
      dueDate: typeof o.dueDate === 'string' ? o.dueDate : '',
      color: typeof o.color === 'string' ? o.color : '#10504a',
      notes,
      coverImageId,
      rewardEnabled: o.rewardEnabled === true,
      rewardCoins: o.rewardCoins != null ? Number(o.rewardCoins) || 0 : undefined,
      rewardItemIds: projectRewardItemIds,
      rewardGrantedStudentIds: projectGrantedIds,
      assignedMemberIds,
      subTasks,
      studentStatusesBySubTask: bySub,
      ...(challengeProjectId ? { challenge: { projectId: challengeProjectId } } : {}),
      createdAt,
      updatedAt,
    };
  }

  if (kind === 'routine') {
    const routineDays = Array.isArray(o.routineDays)
      ? o.routineDays.map((n) => Number(n)).filter((n) => n >= 0 && n <= 6)
      : [];
    const dateStatuses = Array.isArray(o.dateStatuses)
      ? o.dateStatuses
          .map((b) => {
            if (!b || typeof b !== 'object') return null;
            const r = b as Record<string, unknown>;
            const date = typeof r.date === 'string' ? r.date : '';
            if (!date) return null;
            return {
              date,
              studentStatuses: Array.isArray(r.studentStatuses)
                ? r.studentStatuses
                    .map(normalizeStatus)
                    .filter((s): s is AssignmentStudentStatus => s != null)
                : [],
            };
          })
          .filter((b): b is NonNullable<typeof b> => b != null)
      : [];
    const routineRewardItemIds = Array.isArray(o.rewardItemIds)
      ? o.rewardItemIds.filter((x): x is string => typeof x === 'string')
      : undefined;
    const routineGrantedIds = Array.isArray(o.rewardGrantedStudentIds)
      ? o.rewardGrantedStudentIds.filter((x): x is string => typeof x === 'string')
      : undefined;
    return {
      id,
      kind: 'routine',
      title,
      startDate: typeof o.startDate === 'string' ? o.startDate : '',
      dueDate: typeof o.dueDate === 'string' ? o.dueDate : '',
      routineDays,
      basePoints: Number(o.basePoints) || 0,
      categoryId: typeof o.categoryId === 'string' ? o.categoryId : undefined,
      notes,
      coverImageId,
      rewardEnabled: o.rewardEnabled === true,
      rewardCoins: o.rewardCoins != null ? Number(o.rewardCoins) || 0 : undefined,
      rewardItemIds: routineRewardItemIds,
      rewardCompletionRate:
        o.rewardCompletionRate != null
          ? Math.min(100, Math.max(0, Number(o.rewardCompletionRate) || 100))
          : undefined,
      rewardGrantedStudentIds: routineGrantedIds,
      assignedMemberIds,
      dateStatuses,
      createdAt,
      updatedAt,
    };
  }

  return null;
}

function normalizeCoverImage(raw: unknown): AssignmentCoverImage | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = typeof r.id === 'string' ? r.id.trim() : '';
  const url = typeof r.url === 'string' ? r.url.trim() : '';
  const path =
    typeof r.path === 'string' && r.path.trim()
      ? r.path.trim()
      : typeof r.storagePath === 'string'
        ? r.storagePath.trim()
        : '';
  const createdAt = typeof r.createdAt === 'string' ? r.createdAt : '';
  if (!id || !url || !path || !createdAt) return null;
  return { id, path, url, createdAt };
}

export function normalizeAssignmentPointsNoteDoc(
  raw: unknown,
  workspaceId: string,
  gradeId: string
): AssignmentPointsNoteDoc {
  const empty = emptyAssignmentPointsNoteDoc(workspaceId, gradeId);
  if (!raw || typeof raw !== 'object') return empty;
  const o = raw as Record<string, unknown>;
  const coverImages = Array.isArray(o.coverImages)
    ? o.coverImages
        .map(normalizeCoverImage)
        .filter((c): c is AssignmentCoverImage => c != null)
        .slice(0, ASSIGNMENT_COVER_IMAGE_MAX)
    : [];
  return {
    workspaceId,
    gradeId,
    settings: normalizeSettings(o.settings),
    items: Array.isArray(o.items)
      ? o.items.map(normalizeItem).filter((i): i is AssignmentPointsItem => i != null)
      : [],
    coverImages,
    updatedAt: typeof o.updatedAt === 'string' ? o.updatedAt : empty.updatedAt,
  };
}

export function loadAssignmentPointsNoteCache(
  workspaceId: string,
  gradeId: string
): AssignmentPointsNoteDoc | null {
  if (!workspaceId || !gradeId || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey(workspaceId, gradeId));
    if (!raw) return null;
    return normalizeAssignmentPointsNoteDoc(JSON.parse(raw), workspaceId, gradeId);
  } catch {
    return null;
  }
}

export function saveAssignmentPointsNoteCache(
  doc: AssignmentPointsNoteDoc,
  options?: { force?: boolean }
): void {
  if (!doc.workspaceId || !doc.gradeId || typeof window === 'undefined') return;
  if (!options?.force && !canWriteSchoolScopedDataCache(doc.workspaceId)) return;
  try {
    window.localStorage.setItem(storageKey(doc.workspaceId, doc.gradeId), JSON.stringify(doc));
  } catch {
    // ignore quota
  }
}
